import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Toast, type ToastType } from '../components/primitives/Toast';
import { creditsRefusalMessage, isCreditsRefusal } from '../lib/creditsRefusal';
import { setGlobalToastHandler } from '../lib/toastEmitter';
import { useTranslation } from './TranslationContext';

interface ToastConfig {
  message: string;
  type?: ToastType;
  duration?: number;
  /** Makes the bubble actionable — see Toast. Omit and it stays inert. */
  onPress?: () => void;
}

interface ToastContextType {
  showToast: (config: ToastConfig) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const { t, language } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [config, setConfig] = useState<ToastConfig>({
    message: '',
    type: 'info',
    duration: 3000,
  });

  const showToast = useCallback(
    ({ message, type = 'info', duration = 3000, onPress }: ToastConfig) => {
      setConfig({ message, type, duration, onPress });
      setVisible(true);
    },
    [],
  );

  const showSuccess = useCallback(
    (message: string, duration = 3000) => {
      showToast({ message, type: 'success', duration });
    },
    [showToast],
  );

  const showError = useCallback(
    (message: string, duration = 4000) => {
      showToast({ message, type: 'error', duration });
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string, duration = 3000) => {
      showToast({ message, type: 'info', duration });
    },
    [showToast],
  );

  const showWarning = useCallback(
    (message: string, duration = 3500) => {
      showToast({ message, type: 'warning', duration });
    },
    [showToast],
  );

  const handleHide = () => {
    setVisible(false);
  };

  useEffect(() => {
    setGlobalToastHandler((message: string, error?: unknown) =>
      showError(isCreditsRefusal(error) ? creditsRefusalMessage(error, t, language) : message),
    );
    return () => setGlobalToastHandler(null);
  }, [showError, t, language]);

  const value = useMemo(
    () => ({
      showToast,
      showSuccess,
      showError,
      showInfo,
      showWarning,
    }),
    [showToast, showSuccess, showError, showInfo, showWarning],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        message={config.message}
        type={config.type}
        visible={visible}
        onHide={handleHide}
        duration={config.duration}
        onPress={config.onPress}
      />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
